import React from 'react';
import * as Select from '@radix-ui/react-select';
import { Save, Crosshair, AlertOctagon, ChevronDown, Check, UserPlus, Trash, Phone } from 'lucide-react';
import { useState, useEffect } from 'react';

export default function ConfigPanel({ config, setConfig, triggerEmergency, onAdminLogin }) {

    // Radix Select handles value changes slightly differently (direct value, not event)
    const handleLocationChange = (value) => {
        setConfig({ ...config, sector: value });
    };

    const handleGPS = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const coords = `${pos.coords.latitude},${pos.coords.longitude}`;
                    setConfig({ ...config, gps: coords });
                    alert('📍 Ubicación activada. Revisa el mapa.');
                },
                (err) => alert('Error GPS: ' + err.message)
            );
        } else {
            alert('GPS no soportado');
        }
    };

    // Contacts Management
    const [contacts, setContacts] = useState([]);
    const [newContact, setNewContact] = useState({ name: '', phone: '' });

    useEffect(() => {
        const saved = localStorage.getItem('emergency_contacts');
        if (saved) setContacts(JSON.parse(saved));
    }, []);

    const addContact = () => {
        if (!newContact.name || !newContact.phone) return alert('Ingrese nombre y teléfono');
        const updated = [...contacts, newContact];
        setContacts(updated);
        localStorage.setItem('emergency_contacts', JSON.stringify(updated));
        setNewContact({ name: '', phone: '' });
    };

    const removeContact = (index) => {
        const updated = contacts.filter((_, i) => i !== index);
        setContacts(updated);
        localStorage.setItem('emergency_contacts', JSON.stringify(updated));
    };

    return (
        <div className="config-panel fade-in">
            <h2>Configuración</h2>

            <div className="card glass-effect">
                <h3>📍 Ubicación del Dispositivo</h3>
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Barrio / Sector</label>

                    <Select.Root value={config.sector} onValueChange={handleLocationChange}>
                        <Select.Trigger className="SelectTrigger" aria-label="Sector">
                            <Select.Value placeholder="Seleccione un sector..." />
                            <Select.Icon>
                                <ChevronDown size={16} />
                            </Select.Icon>
                        </Select.Trigger>

                        <Select.Portal>
                            <Select.Content className="SelectContent">
                                <Select.Viewport>
                                    <SelectItem value="gazcue">Gazcue (Zona Alta)</SelectItem>
                                    <SelectItem value="villa-mella">Villa Mella (Riesgo Medio)</SelectItem>
                                    <SelectItem value="los-prados">Los Prados (Zona Baja)</SelectItem>
                                </Select.Viewport>
                            </Select.Content>
                        </Select.Portal>
                    </Select.Root>
                </div>

                <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Coordenadas GPS</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <input
                            type="text"
                            value={config.gps || ''}
                            placeholder="Lat, Lng"
                            readOnly
                            className="input-text"
                        />
                        <button onClick={handleGPS} className="btn btn-secondary full-width" style={{ justifyContent: 'center' }}>
                            <Crosshair size={18} /> Activar Geolocalización
                        </button>
                    </div>
                </div>
            </div>

            <div className="card glass-effect mt-4">
                <h3>🧪 Pruebas de Sistema</h3>
                <button
                    className="btn btn-danger full-width"
                    onClick={() => triggerEmergency(1, 'SIMULACIÓN: Riesgo Crítico Detectado')}
                >
                    <AlertOctagon size={20} /> Simular Alerta de Inundación
                </button>
            </div>

            <div className="card glass-effect mt-4">
                <h3>📞 Contactos de Emergencia</h3>
                <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: 10 }}>Se enviará tu ubicación en tiempo real a estos contactos al pulsar "Necesito Ayuda".</p>

                <div style={{ display: 'grid', gap: 10, marginBottom: 15 }}>
                    {contacts.map((c, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '8px 12px', borderRadius: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Phone size={16} color="#4ade80" />
                                <div>
                                    <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{c.name}</div>
                                    <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{c.phone}</div>
                                </div>
                            </div>
                            <button onClick={() => removeContact(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
                                <Trash size={16} />
                            </button>
                        </div>
                    ))}
                    {contacts.length === 0 && <span style={{ fontStyle: 'italic', color: '#64748b' }}>Sin contactos registrados.</span>}
                </div>

                <div style={{ display: 'flex', gap: 5 }}>
                    <input
                        className="input-text"
                        placeholder="Nombre"
                        value={newContact.name}
                        onChange={e => setNewContact({ ...newContact, name: e.target.value })}
                        style={{ flex: 1 }}
                    />
                    <input
                        className="input-text"
                        placeholder="Teléfono"
                        title="Teléfono con código de área"
                        value={newContact.phone}
                        onChange={e => setNewContact({ ...newContact, phone: e.target.value })}
                        style={{ flex: 1 }}
                    />
                    <button onClick={addContact} className="btn btn-primary" style={{ padding: '0 15px' }}>
                        <UserPlus size={18} />
                    </button>
                </div>
            </div>

            <div className="card glass-effect mt-4">
                <h3>📡 MQTT (Avanzado)</h3>
                <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Configuración gestionada automáticamente en esta versión.</p>
            </div>

            <div style={{ marginTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem' }}>
                <button
                    onClick={onAdminLogin}
                    style={{
                        background: 'none', border: 'none', color: '#475569',
                        fontSize: '0.8rem', cursor: 'pointer', width: '100%', textAlign: 'center'
                    }}
                >
                    🔒 Acceso Administrativo
                </button>
            </div>
        </div>
    );
}

// Reusable Select Item Component
const SelectItem = React.forwardRef(({ children, className, ...props }, ref) => {
    return (
        <Select.Item className={`SelectItem ${className || ''}`} {...props} ref={ref}>
            <Select.ItemText>{children}</Select.ItemText>
            <Select.ItemIndicator className="SelectItemIndicator">
                <Check size={16} />
            </Select.ItemIndicator>
        </Select.Item>
    );
});
